// Lightweight client-side Upsell System

/**
 * @typedef {Object} UpsellOffer
 * @property {string} id
 * @property {string} title
 * @property {string} description
 * @property {number} credits
 * @property {string} value
 * @property {string} ctaText
 * @property {{ condition: 'post_processing'|'high_satisfaction'|'repeat_user'|'file_type', parameters?: Record<string, any> }} trigger
 * @property {number} priority
 */

/**
 * @typedef {Object} ProcessingResult
 * @property {string} jobType
 * @property {string=} enhancementType
 * @property {Object=} result
 * @property {string=} resultText
 */

/**
 * @typedef {Object} UserProfile
 * @property {number} filesProcessedThisMonth
 * @property {string} detectedLanguage
 * @property {boolean} hasInternationalTeam
 * @property {number} credits
 */

const normalizeText = (t) => (t || '').toLowerCase();

const shouldOfferExecutiveSummary = (processingResult) => {
  const jt = processingResult.jobType || '';
  const enh = processingResult.enhancementType || '';
  const text = normalizeText(processingResult.resultText || (processingResult.result?.enhancement_suggestions || ''));

  // Only offer executive summary for document jobs, not Excel jobs
  const isDocumentJob = jt.startsWith('document_');
  const isExcelJob = jt.startsWith('excel_');

  // Don't offer executive summary for Excel jobs
  if (isExcelJob) {
    return false;
  }

  // Offer for all document jobs (PDF and Word)
  const longEnough = (processingResult.resultText || '').length + (processingResult.result?.enhancement_suggestions || '').length > 500;
  const alreadyHas = text.includes('executive summary');

  // Offer for all document jobs that are long enough and don't already have one
  return isDocumentJob && longEnough && !alreadyHas;
};

const shouldOfferTranslation = (processingResult, userProfile) => {
  const nonEnglish = (userProfile.detectedLanguage || 'en').toLowerCase() !== 'en';
  const longDoc = (processingResult.resultText || '').length + (processingResult.result?.enhancement_suggestions || '').length > 200;
  const hasContent = (processingResult.resultText || '').length > 50 || (processingResult.result?.enhancement_suggestions || '').length > 50;
  // Always offer translation for completed jobs with content
  return hasContent; // Show translation if there's content to translate
};

const shouldOfferStyleThemes = (processingResult) => {
  const jt = processingResult.jobType || '';
  const hasContent = (processingResult.resultText || '').length > 50 || (processingResult.result?.enhancement_suggestions || '').length > 50;
  const alreadyHasTheme = processingResult.result?.styledTheme;

  // Only offer style themes for document jobs, not Excel jobs
  const isDocumentJob = jt.startsWith('document_');
  const isExcelJob = jt.startsWith('excel_');

  // Don't offer style themes for Excel jobs
  if (isExcelJob) {
    return false;
  }

  return isDocumentJob && hasContent && !alreadyHasTheme;
};

/**
 * @param {ProcessingResult} processingResult
 * @param {UserProfile} userProfile
 * @param {number=} satisfactionScore
 * @returns {UpsellOffer[]}
 */
export function getRelevantUpsells(processingResult, userProfile, satisfactionScore) {
  const offers = [];

  if (shouldOfferExecutiveSummary(processingResult)) {
    offers.push({
      id: 'executive_summary',
      title: 'Add Executive Summary',
      description: 'Get a 3-5 bullet point executive summary at the top of your document',
      credits: 5,
      value: 'Perfect for stakeholder presentations',
      ctaText: 'Add Summary (+5 credits)',
      trigger: { condition: 'post_processing' },
      priority: 1,
    });
  }

  if (shouldOfferStyleThemes(processingResult)) {
    offers.push({
      id: 'style_themes',
      title: 'Apply Professional Theme',
      description: 'Choose from Modern Corporate, Academic Journal, or Creative themes',
      credits: 8,
      value: 'Stand out with professional styling',
      ctaText: 'Browse Themes (+8 credits)',
      trigger: { condition: 'post_processing' },
      priority: 2,
    });
  }

  if (shouldOfferTranslation(processingResult, userProfile)) {
    offers.push({
      id: 'translation',
      title: 'Translate Document',
      description: 'Translate to Spanish, French, German, or 20+ other languages',
      credits: 10,
      value: 'Reach global audiences',
      ctaText: 'Translate Now (+10 credits)',
      trigger: { condition: 'post_processing' },
      priority: 3,
    });
  }


  if ((userProfile.filesProcessedThisMonth || 0) > 5) {
    offers.push({
      id: 'bulk_processing',
      title: 'Process Multiple Files',
      description: 'Upload a ZIP file and process up to 10 documents at once',
      credits: 25,
      value: 'Save time on batch processing',
      ctaText: 'Try Bulk Processing (+25 credits)',
      trigger: { condition: 'repeat_user' },
      priority: 5,
    });
  }

  return offers.sort((a, b) => a.priority - b.priority).slice(0, 3);
}

export function getDefaultUserProfile(user, filesProcessedThisMonth = 0) {
  return {
    filesProcessedThisMonth,
    detectedLanguage: (navigator.language || 'en').slice(0, 2),
    hasInternationalTeam: false,
    credits: user?.credits ?? 0,
  };
}

export const ALL_LANG_OPTIONS = [
  { code: 'af', name: 'Afrikaans' },
  { code: 'sq', name: 'Albanian' },
  { code: 'am', name: 'Amharic' },
  { code: 'ar', name: 'Arabic' },
  { code: 'hy', name: 'Armenian' },
  { code: 'az', name: 'Azerbaijani' },
  { code: 'eu', name: 'Basque' },
  { code: 'be', name: 'Belarusian' },
  { code: 'bn', name: 'Bengali' },
  { code: 'bs', name: 'Bosnian' },
  { code: 'bg', name: 'Bulgarian' },
  { code: 'ca', name: 'Catalan' },
  { code: 'ceb', name: 'Cebuano' },
  { code: 'ny', name: 'Chichewa' },
  { code: 'zh', name: 'Chinese' },
  { code: 'zh-CN', name: 'Chinese (Simplified)' },
  { code: 'zh-TW', name: 'Chinese (Traditional)' },
  { code: 'co', name: 'Corsican' },
  { code: 'hr', name: 'Croatian' },
  { code: 'cs', name: 'Czech' },
  { code: 'da', name: 'Danish' },
  { code: 'nl', name: 'Dutch' },
  { code: 'en', name: 'English' },
  { code: 'eo', name: 'Esperanto' },
  { code: 'et', name: 'Estonian' },
  { code: 'fi', name: 'Finnish' },
  { code: 'fr', name: 'French' },
  { code: 'fy', name: 'Frisian' },
  { code: 'gl', name: 'Galician' },
  { code: 'ka', name: 'Georgian' },
  { code: 'de', name: 'German' },
  { code: 'el', name: 'Greek' },
  { code: 'gu', name: 'Gujarati' },
  { code: 'ht', name: 'Haitian Creole' },
  { code: 'ha', name: 'Hausa' },
  { code: 'haw', name: 'Hawaiian' },
  { code: 'he', name: 'Hebrew' },
  { code: 'hi', name: 'Hindi' },
  { code: 'hmn', name: 'Hmong' },
  { code: 'hu', name: 'Hungarian' },
  { code: 'is', name: 'Icelandic' },
  { code: 'ig', name: 'Igbo' },
  { code: 'id', name: 'Indonesian' },
  { code: 'ga', name: 'Irish' },
  { code: 'it', name: 'Italian' },
  { code: 'ja', name: 'Japanese' },
  { code: 'jw', name: 'Javanese' },
  { code: 'kn', name: 'Kannada' },
  { code: 'kk', name: 'Kazakh' },
  { code: 'km', name: 'Khmer' },
  { code: 'ko', name: 'Korean' },
  { code: 'ku', name: 'Kurdish' },
  { code: 'ky', name: 'Kyrgyz' },
  { code: 'lo', name: 'Lao' },
  { code: 'la', name: 'Latin' },
  { code: 'lv', name: 'Latvian' },
  { code: 'lt', name: 'Lithuanian' },
  { code: 'lb', name: 'Luxembourgish' },
  { code: 'mk', name: 'Macedonian' },
  { code: 'mg', name: 'Malagasy' },
  { code: 'ms', name: 'Malay' },
  { code: 'ml', name: 'Malayalam' },
  { code: 'mt', name: 'Maltese' },
  { code: 'mi', name: 'Maori' },
  { code: 'mr', name: 'Marathi' },
  { code: 'mn', name: 'Mongolian' },
  { code: 'my', name: 'Myanmar (Burmese)' },
  { code: 'ne', name: 'Nepali' },
  { code: 'no', name: 'Norwegian' },
  { code: 'ps', name: 'Pashto' },
  { code: 'fa', name: 'Persian' },
  { code: 'pl', name: 'Polish' },
  { code: 'pt', name: 'Portuguese' },
  { code: 'pa', name: 'Punjabi' },
  { code: 'ro', name: 'Romanian' },
  { code: 'ru', name: 'Russian' },
  { code: 'sm', name: 'Samoan' },
  { code: 'gd', name: 'Scots Gaelic' },
  { code: 'sr', name: 'Serbian' },
  { code: 'st', name: 'Sesotho' },
  { code: 'sn', name: 'Shona' },
  { code: 'sd', name: 'Sindhi' },
  { code: 'si', name: 'Sinhala' },
  { code: 'sk', name: 'Slovak' },
  { code: 'sl', name: 'Slovenian' },
  { code: 'so', name: 'Somali' },
  { code: 'es', name: 'Spanish' },
  { code: 'su', name: 'Sundanese' },
  { code: 'sw', name: 'Swahili' },
  { code: 'sv', name: 'Swedish' },
  { code: 'tl', name: 'Tagalog' },
  { code: 'tg', name: 'Tajik' },
  { code: 'ta', name: 'Tamil' },
  { code: 'tt', name: 'Tatar' },
  { code: 'te', name: 'Telugu' },
  { code: 'th', name: 'Thai' },
  { code: 'tr', name: 'Turkish' },
  { code: 'tk', name: 'Turkmen' },
  { code: 'uk', name: 'Ukrainian' },
  { code: 'ur', name: 'Urdu' },
  { code: 'ug', name: 'Uyghur' },
  { code: 'uz', name: 'Uzbek' },
  { code: 'vi', name: 'Vietnamese' },
  { code: 'cy', name: 'Welsh' },
  { code: 'xh', name: 'Xhosa' },
  { code: 'yi', name: 'Yiddish' },
  { code: 'yo', name: 'Yoruba' },
  { code: 'zu', name: 'Zulu' }
]; 